import { type PaperColor, paperColors, type SchoolSettingsBody } from '@aurea/contracts';
import { Button, Field, Input, Select, useToast } from '@aurea/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { ImageUp, Trash2 } from 'lucide-react';
import { type ChangeEvent, type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useErrorText } from '../../lib/i18n';
import { meQuery, useMe } from '../_app';

export const Route = createFileRoute('/_app/settings')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me.roles.includes('admin')) throw redirect({ to: '/' });
  },
  component: Settings,
});

const ZONES = [
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Samara',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Novosibirsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
  'Europe/Minsk',
  'Asia/Almaty',
  'Asia/Tashkent',
  'Asia/Tbilisi',
  'Asia/Yerevan',
  'Europe/Berlin',
  'Europe/London',
  'UTC',
];

const offset = (tz: string) => {
  try {
    const part = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return part?.value.replace('GMT', 'UTC') ?? '';
  } catch {
    return '';
  }
};

/** Логотип ужимаем в браузере до 256×256 WebP — так он всегда влезает в лимит и быстро грузится. */
async function shrinkLogo(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const k = Math.min(1, 256 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * k);
  canvas.height = Math.round(bitmap.height * k);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/webp', 0.9);
}

const RULES = ['chargeAbsent', 'teacherCanReschedule', 'showGroupMembers', 'notifyLessonChanges'] as const;

function Settings() {
  const me = useMe();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const errorText = useErrorText();
  const s = me.school;
  const [color, setColor] = useState<PaperColor | null>(s.accentColor);
  const [name, setName] = useState(s.name);
  const [hoursError, setHoursError] = useState(false);
  const zones = ZONES.includes(me.timezone) ? ZONES : [me.timezone, ...ZONES];

  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['me'] }),
      qc.invalidateQueries({ queryKey: ['school-public'] }),
    ]);
  const save = useMutation({
    mutationFn: api.saveSchoolSettings,
    onSuccess: async () => {
      await refresh();
      toast(t('settings.saved'));
    },
  });
  const upload = useMutation({
    mutationFn: async (file: File) => api.uploadLogo({ dataUrl: await shrinkLogo(file) }),
    onSuccess: refresh,
  });
  const removeLogo = useMutation({ mutationFn: api.deleteLogo, onSuccess: refresh });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => String(f.get(k) ?? '');
    const body: SchoolSettingsBody = {
      name: name.trim(),
      accentColor: color,
      timezone: v('timezone'),
      defaultLocale: v('defaultLocale') === 'en' ? 'en' : 'ru',
      settings: {
        workdayStart: v('workdayStart'),
        workdayEnd: v('workdayEnd'),
        chargeAbsent: f.has('chargeAbsent'),
        teacherCanReschedule: f.has('teacherCanReschedule'),
        showGroupMembers: f.has('showGroupMembers'),
        notifyLessonChanges: f.has('notifyLessonChanges'),
      },
    };
    const bad = body.settings.workdayStart >= body.settings.workdayEnd;
    setHoursError(bad);
    if (!bad) save.mutate(body);
  }

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) upload.mutate(file);
  }

  return (
    <>
      <header className="page-head">
        <h1>{t('settings.title')}</h1>
      </header>

      <form className="settings" onSubmit={onSubmit}>
        <section className="tray tray-pad settings-block" aria-labelledby="look-h">
          <h2 id="look-h">{t('settings.look')}</h2>
          <div className="settings-look">
            <div className="settings-preview" aria-hidden="true">
              <div className="school-badge">
                {s.logoUrl ? (
                  <img className="logo" src={s.logoUrl} alt="" />
                ) : (
                  <div className="logo paper sticker" data-c={color ?? 'lilac'}>
                    {name.charAt(0)}
                  </div>
                )}
                <div className="min-w-0">
                  <b className="block truncate leading-tight">{name || '—'}</b>
                  <span className="block truncate text-xs text-ink-3">{location.host}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <Field label={t('settings.name')}>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  minLength={2}
                  maxLength={80}
                />
              </Field>
              <fieldset className="flex flex-col gap-2">
                <legend className="mb-2 text-sm font-medium text-ink-2">{t('settings.color')}</legend>
                <div className="flex flex-wrap gap-2">
                  {paperColors.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="swatch paper sticker"
                      data-c={c}
                      aria-pressed={color === c}
                      aria-label={t(`colors.${c}`)}
                      title={t(`colors.${c}`)}
                      onClick={() => setColor(c)}
                    />
                  ))}
                </div>
              </fieldset>
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-ink-2">{t('settings.logo')}</span>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="btn btn-ghost">
                    <ImageUp className="i" aria-hidden="true" />
                    {s.logoUrl ? t('settings.logoReplace') : t('settings.logoUpload')}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="sr-only"
                      onChange={onFile}
                    />
                  </label>
                  {s.logoUrl && (
                    <Button type="button" variant="ghost" onClick={() => removeLogo.mutate()}>
                      <Trash2 className="i" aria-hidden="true" />
                      {t('settings.logoRemove')}
                    </Button>
                  )}
                </div>
                <small className="text-xs text-ink-3">
                  {upload.isError ? errorText(upload.error) : t('settings.logoHint')}
                </small>
              </div>
            </div>
          </div>
        </section>

        <section className="tray tray-pad settings-block" aria-labelledby="time-h">
          <h2 id="time-h">{t('settings.timeAndLanguage')}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('settings.timezone')}>
              <Select name="timezone" defaultValue={me.timezone}>
                {zones.map((z) => (
                  <option key={z} value={z}>
                    {z.replace('_', ' ')} ({offset(z)})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('settings.locale')} hint={t('settings.localeHint')}>
              <Select name="defaultLocale" defaultValue={me.school.defaultLocale}>
                <option value="ru">Русский</option>
                <option value="en">English</option>
              </Select>
            </Field>
            <Field label={t('settings.workdayStart')}>
              <Input
                name="workdayStart"
                type="time"
                step={1800}
                defaultValue={s.settings.workdayStart}
                required
              />
            </Field>
            <Field
              label={t('settings.workdayEnd')}
              hint={t('settings.workdayHint')}
              error={hoursError ? t('settings.workdayOrder') : undefined}
            >
              <Input
                name="workdayEnd"
                type="time"
                step={1800}
                defaultValue={s.settings.workdayEnd}
                required
              />
            </Field>
          </div>
        </section>

        <section className="tray tray-pad settings-block" aria-labelledby="rules-h">
          <h2 id="rules-h">{t('settings.rules')}</h2>
          <ul className="settings-rules">
            {RULES.map((k) => (
              <li key={k}>
                <label className="check">
                  <input type="checkbox" name={k} defaultChecked={s.settings[k]} />
                  <span>
                    <b>{t(`settings.rule.${k}`)}</b>
                    <small>{t(`settings.ruleHint.${k}`)}</small>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </section>

        <div className="settings-save">
          <Button type="submit" loading={save.isPending}>
            {t('settings.save')}
          </Button>
          {save.isError && (
            <p className="text-sm text-danger" role="alert">
              {errorText(save.error)}
            </p>
          )}
        </div>
      </form>
    </>
  );
}
